import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  HomeIcon,
  LockClosedIcon,
  ReloadIcon,
} from '@radix-ui/react-icons';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

type StatusCode = 401 | 403 | 404 | 500;

interface StatusAction {
  label: string;
  to?: string;
  onClick?: () => void;
  variant?: 'default' | 'outline' | 'ghost';
}

interface StatusPageProps {
  code: StatusCode;
  title?: string;
  description?: string;
  eyebrow?: string;
  primaryAction?: StatusAction;
  secondaryAction?: StatusAction;
  fullScreen?: boolean;
}

const STATUS_COPY: Record<StatusCode, { eyebrow: string; title: string; description: string }> = {
  401: {
    eyebrow: '401 / Unauthorized',
    title: '需要登录后继续',
    description: '当前页面包含你的项目、资产或任务数据。登录后会回到刚才访问的位置。',
  },
  403: {
    eyebrow: '403 / Forbidden',
    title: '没有访问权限',
    description: '你的账号目前没有访问该页面的权限。如需开通权限，请联系管理员。',
  },
  404: {
    eyebrow: '404 / Not Found',
    title: '页面不存在',
    description: '这个地址没有对应的页面。你可以回到视频项目，继续当前创作流程。',
  },
  500: {
    eyebrow: '500 / Runtime Error',
    title: '页面暂时不可用',
    description: '页面渲染时遇到异常。你可以重试当前页面，或先回到首页。',
  },
};

function StatusActionButton({ action }: { action: StatusAction }) {
  const button = (
    <Button
      type="button"
      variant={action.variant || 'default'}
      onClick={action.onClick}
      className="min-w-28"
    >
      {action.label}
    </Button>
  );

  if (!action.to) {
    return button;
  }

  return (
    <Button asChild variant={action.variant || 'default'} className="min-w-28">
      <Link to={action.to}>{action.label}</Link>
    </Button>
  );
}

function StatusIcon({ code }: { code: StatusCode }) {
  if (code === 401 || code === 403) {
    return <LockClosedIcon className="h-7 w-7" />;
  }

  if (code === 500) {
    return <ReloadIcon className="h-7 w-7" />;
  }

  return <ArrowLeftIcon className="h-7 w-7" />;
}

const StatusPage: React.FC<StatusPageProps> = ({
  code,
  title,
  description,
  eyebrow,
  primaryAction,
  secondaryAction,
  fullScreen = false,
}) => {
  const copy = STATUS_COPY[code];
  const resolvedPrimaryAction = primaryAction || {
    label: code === 401 ? '去登录' : '返回我的项目',
    to: code === 401 ? '/login' : '/video-projects',
  };
  const resolvedSecondaryAction = secondaryAction || {
    label: '回到首页',
    to: '/',
    variant: 'outline' as const,
  };

  return (
    <section
      className={cn(
        'flex min-h-[calc(100vh-3.5rem)] items-center bg-surface-primary px-4 py-12 text-content-primary sm:px-6 lg:px-8',
        fullScreen && 'min-h-screen'
      )}
      aria-labelledby={`status-${code}-title`}
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center text-center">
        <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-lg border border-border-default bg-surface-elevated text-primary shadow-surface-sm">
          <StatusIcon code={code} />
        </div>

        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-content-muted">
          {eyebrow || copy.eyebrow}
        </p>
        <h1 id={`status-${code}-title`} className="max-w-2xl text-3xl font-bold tracking-tight text-content-primary sm:text-4xl">
          {title || copy.title}
        </h1>
        <p className="mt-4 max-w-xl text-sm leading-6 text-content-secondary sm:text-base">
          {description || copy.description}
        </p>

        <div className="mt-8 flex w-full flex-col-reverse gap-3 sm:w-auto sm:flex-row sm:justify-center">
          <StatusActionButton action={resolvedSecondaryAction} />
          <StatusActionButton action={resolvedPrimaryAction} />
        </div>

        <div className="mt-10 flex items-center gap-2 text-xs text-content-muted">
          <HomeIcon className="h-3.5 w-3.5" />
          CultureClick
        </div>
      </div>
    </section>
  );
};

export default StatusPage;
